import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CertificationLanding.css';

const PILLARS = [
  {
    id: 'voice_safety',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
    title: 'Voice Safety',
    tagline: 'Speak openly, without fear',
    description: 'Employees can share ideas, raise concerns, and disagree constructively without fear of retaliation or being silenced.',
    signals: ['Open feedback culture', 'Ideas are heard and valued', 'Dissent is welcomed'],
    color: '#10b981'
  },
  {
    id: 'emotional_safety',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    title: 'Emotional Safety',
    tagline: 'No humiliation, no toxicity',
    description: 'A workplace free from shouting, public humiliation, passive aggression, and emotional manipulation.',
    signals: ['Respectful communication', 'Zero tolerance for bullying', 'Supportive management'],
    color: '#06b6d4'
  },
  {
    id: 'growth_safety',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    title: 'Growth Safety',
    tagline: 'Mistakes are learning, not failure',
    description: 'Employees can take risks, make mistakes, and grow without fear of punishment or blame.',
    signals: ['Learning from failures', 'Career development support', 'Innovation encouraged'],
    color: '#8b5cf6'
  },
  {
    id: 'boundary_safety',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    title: 'Boundary Safety',
    tagline: 'No forced overtime, no guilt',
    description: 'Respecting personal time, no guilt-tripping for taking leave, and sustainable work expectations.',
    signals: ['Healthy work-life balance', 'Respected personal time', 'No overwork culture'],
    color: '#f59e0b'
  },
  {
    id: 'identity_safety',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    title: 'Identity Safety',
    tagline: 'No bias, no discrimination',
    description: 'An inclusive environment free from sexism, ageism, casteism, or any form of identity-based discrimination.',
    signals: ['Equal opportunity', 'Inclusive policies', 'Diverse representation'],
    color: '#ec4899'
  }
];

const TIERS = [
  { stars: 1, label: '1-Star', requirement: 'At least 2 pillars score >= 60' },
  { stars: 2, label: '2-Star', requirement: 'At least 3 pillars score >= 60' },
  { stars: 3, label: '3-Star', requirement: 'At least 4 pillars score >= 65' },
  { stars: 4, label: '4-Star', requirement: 'All 5 pillars score >= 70' },
  { stars: 5, label: '5-Star', requirement: 'All 5 pillars score >= 80' }
];

const STEPS = [
  { num: '01', title: 'Deploy Elo', description: 'Introduce Mend as your AI-powered employee well-being companion. Employees engage naturally through private conversations.' },
  { num: '02', title: 'AI Analyzes Conversations', description: 'Our AI engine processes anonymized conversation patterns across all five psychological safety pillars using advanced NLP.' },
  { num: '03', title: 'Get Certified', description: 'Receive your tier-based certification (1-5 Stars) with a detailed breakdown, downloadable certificate, embeddable badge, and public profile.' }
];

const FAQS = [
  { q: 'How is psychological safety measured?', a: 'We analyze anonymized patterns from employee conversations with Mend across five distinct pillars. Our AI engine combines natural language analysis, sentiment tracking, behavioral signals, and engagement metrics to produce scientifically-grounded scores.' },
  { q: 'Is employee data kept private?', a: 'Absolutely. Individual conversations are never exposed. Our certification engine works with aggregated, anonymized patterns only. No manager or admin ever sees specific employee messages.' },
  { q: 'How long is the certification valid?', a: 'Each certification is valid for 1 year from the evaluation date. Organizations are encouraged to re-certify annually to demonstrate ongoing commitment.' },
  { q: 'What\'s the minimum number of employees needed?', a: 'There is no strict minimum. However, more employee participation leads to more accurate and representative certification scores.' },
  { q: 'How is this different from Great Place to Work?', a: 'Unlike survey-based certifications, our approach uses AI-driven conversational analysis — capturing real, unfiltered employee experiences over time rather than one-time survey responses.' },
  { q: 'Can we display the certification publicly?', a: 'Yes! Certified organizations receive an embeddable badge, a downloadable PDF certificate, and a public profile page they can share with candidates, clients, and stakeholders.' }
];

const StarDisplay = ({ count, size = 18 }) => (
  <span className="cl-stars">
    {[...Array(5)].map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" width={size} height={size} fill={i < count ? '#f59e0b' : '#e5e7eb'} stroke={i < count ? '#f59e0b' : '#d1d5db'} strokeWidth="0.5">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
      </svg>
    ))}
  </span>
);

const CertificationLanding = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="cl-page">
      {/* Navbar */}
      <nav className="cl-nav">
        <div className="cl-nav-inner">
          <div className="cl-nav-brand" onClick={() => navigate('/')}>
            <div className="cl-nav-logo">R</div>
            <span>Mend</span>
          </div>
          <div className="cl-nav-links">
            <a href="#pillars">Pillars</a>
            <a href="#how-it-works">Process</a>
            <a href="#tiers">Tiers</a>
            <a href="#faq">FAQ</a>
            <button className="cl-nav-cta" onClick={() => navigate('/certification/dashboard')}>Get Certified</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="cl-hero">
        <div className="cl-hero-deco">
          <div className="cl-hero-circle cl-hero-circle-1" />
          <div className="cl-hero-circle cl-hero-circle-2" />
          <div className="cl-hero-circle cl-hero-circle-3" />
        </div>
        <div className="cl-hero-content">
          <div className="cl-hero-tag">
            <svg viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.5" width="16" height="16"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Certification Program
          </div>
          <h1>Psychologically Safe<br /><span>Workplace™</span></h1>
          <p className="cl-hero-sub">The world's first AI-driven psychological safety certification. Measure what matters. Certify what's real.</p>
          <div className="cl-hero-btns">
            <button className="cl-btn-primary" onClick={() => navigate('/certification/dashboard')}>
              Start Certification
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
            <a href="#how-it-works" className="cl-btn-outline">Learn More</a>
          </div>
          <div className="cl-hero-stats">
            <div className="cl-stat"><span className="cl-stat-num">5</span><span className="cl-stat-lbl">Safety Pillars</span></div>
            <div className="cl-stat-sep" />
            <div className="cl-stat"><span className="cl-stat-num">AI</span><span className="cl-stat-lbl">Driven Analysis</span></div>
            <div className="cl-stat-sep" />
            <div className="cl-stat"><span className="cl-stat-num">1yr</span><span className="cl-stat-lbl">Validity</span></div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="cl-section" id="pillars">
        <div className="cl-container">
          <div className="cl-section-head">
            <span className="cl-tag">The Framework</span>
            <h2>5 Pillars of Psychological Safety</h2>
            <p>Our certification evaluates organizations across five scientifically-grounded dimensions of workplace psychological safety.</p>
          </div>
          <div className="cl-pillars-grid">
            {PILLARS.map((p) => (
              <div key={p.id} className="cl-pillar-card" style={{ '--pcolor': p.color }}>
                <div className="cl-pillar-icon" style={{ background: p.color + '14', color: p.color }}>{p.icon}</div>
                <h3>{p.title}</h3>
                <p className="cl-pillar-tag">{p.tagline}</p>
                <p className="cl-pillar-desc">{p.description}</p>
                <ul className="cl-pillar-signals">
                  {p.signals.map((s, j) => (
                    <li key={j}>
                      <svg viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth="2" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="cl-section cl-section-light" id="how-it-works">
        <div className="cl-container">
          <div className="cl-section-head">
            <span className="cl-tag">The Process</span>
            <h2>How It Works</h2>
            <p>Three simple steps from deployment to certification.</p>
          </div>
          <div className="cl-steps-row">
            {STEPS.map((step, i) => (
              <div key={i} className="cl-step">
                <div className="cl-step-num">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {i < 2 && <div className="cl-step-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="cl-section" id="tiers">
        <div className="cl-container">
          <div className="cl-section-head">
            <span className="cl-tag">Certification Levels</span>
            <h2>Star Rating Tiers</h2>
            <p>Organizations are rated on a 1-5 star scale based on how many pillars meet minimum thresholds.</p>
          </div>
          <div className="cl-tiers-grid">
            {TIERS.map((t) => (
              <div key={t.stars} className="cl-tier-card">
                <StarDisplay count={t.stars} size={20} />
                <h3>{t.label}</h3>
                <p>{t.requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="cl-section cl-section-light" id="why">
        <div className="cl-container">
          <div className="cl-section-head">
            <span className="cl-tag">The Research</span>
            <h2>Why Psychological Safety Matters</h2>
          </div>
          <div className="cl-stats-grid">
            <div className="cl-stat-card"><div className="cl-stat-big">76%</div><p>higher employee engagement in psychologically safe teams</p></div>
            <div className="cl-stat-card"><div className="cl-stat-big">50%</div><p>more productivity when employees feel safe to take risks</p></div>
            <div className="cl-stat-card"><div className="cl-stat-big">27%</div><p>reduction in turnover with strong psychological safety</p></div>
            <div className="cl-stat-card"><div className="cl-stat-big">3.5x</div><p>more likely to be high-performing (Google's Project Aristotle)</p></div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cl-section" id="faq">
        <div className="cl-container">
          <div className="cl-section-head">
            <span className="cl-tag">Questions</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="cl-faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className={`cl-faq ${openFaq === i ? 'open' : ''}`}>
                <button className="cl-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points={openFaq === i ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                </button>
                <div className="cl-faq-a" style={{ maxHeight: openFaq === i ? '200px' : '0' }}>{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cl-cta">
        <div className="cl-container">
          <h2>Ready to Prove Your Commitment?</h2>
          <p>Start your psychological safety certification journey today.</p>
          <button className="cl-btn-primary cl-btn-lg" onClick={() => navigate('/certification/dashboard')}>
            Start Certification
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </section>

      <footer className="cl-footer">
        <p>&copy; {new Date().getFullYear()} Mend &middot; Psychologically Safe Workplace&trade; Certification</p>
      </footer>
    </div>
  );
};

export default CertificationLanding;
